import React from 'react';
import { Users, Edit3, Trash2, ShieldCheck, ShieldAlert, Clock, AlertCircle, ChevronDown, CheckCircle2 } from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';

interface UserTableProps {
  usersList: User[];
  isLoading: boolean;
  currentUser: User | null;
  onOpenEdit: (user: User) => void;
  onOpenDelete: (user: User) => void;
  onOpenPublisherVerification?: (user: User) => void;
  onToggleVerifyPublisher?: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  usersList,
  isLoading,
  currentUser,
  onOpenEdit,
  onOpenDelete,
  onOpenPublisherVerification,
  onToggleVerifyPublisher,
}) => {
  if (isLoading) {
    return (
      <div className="p-16 text-center">
        <div className="inline-block w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs text-slate-500">Memuat data pengguna sistem...</p>
      </div>
    );
  }

  if (usersList.length === 0) {
    return (
      <div className="p-16 text-center text-slate-500">
        <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="font-semibold text-slate-700 text-sm">Tidak ada user ditemukan</p>
        <p className="text-xs text-slate-400 mt-1">Coba sesuaikan kata kunci pencarian Anda.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50/75 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <th className="py-3 px-5">Nama & Kontak</th>
            <th className="py-3 px-5">Peran Akun</th>
            <th className="py-3 px-5">Organisasi / Penerbit</th>
            <th className="py-3 px-5">Kode UIBN</th>
            <th className="py-3 px-5">Status Verifikasi</th>
            <th className="py-3 px-5">Tanggal Daftar</th>
            <th className="py-3 px-5 text-right">Aksi Kontrol</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-xs text-slate-600">
          {usersList.map((usr: User, uIdx: number) => {
            const initials = usr.name.charAt(0).toUpperCase();
            const isSelf = usr.id === currentUser?.id;

            const roleStyles = ({
              admin: 'bg-emerald-50 text-emerald-700 border-emerald-100',
              penerbit: 'bg-purple-50 text-purple-700 border-purple-100',
              organisasi: 'bg-amber-50 text-amber-800 border-amber-200',
              penulis: 'bg-sky-50 text-sky-700 border-sky-100',
            } as Record<string, string>)[usr.role] || 'bg-slate-50 text-slate-600 border-slate-100';

            const isPubVerified = Boolean(
              usr.isPublisherVerified === 1 ||
              usr.isPublisherVerified === true ||
              usr.publisherVerificationStatus === 'verified'
            );
            const isPubPending = usr.publisherVerificationStatus === 'pending';
            const isPubRejected = usr.publisherVerificationStatus === 'rejected';

            return (
              <tr key={`user-row-${usr.id || uIdx}-${uIdx}`} className="hover:bg-slate-50/40 transition">
                {/* Name & Contact */}
                <td className="py-3.5 px-5">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-xl font-bold flex items-center justify-center shrink-0 shadow-2xs text-xs ${
                        usr.role === 'admin'
                          ? 'bg-gradient-to-tr from-emerald-500 to-teal-400 text-white'
                          : usr.role === 'penerbit'
                          ? 'bg-gradient-to-tr from-purple-500 to-indigo-400 text-white'
                          : usr.role === 'organisasi'
                          ? 'bg-gradient-to-tr from-amber-500 to-orange-400 text-white'
                          : 'bg-gradient-to-tr from-sky-500 to-indigo-400 text-white'
                      }`}
                    >
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                        <span>{usr.name}</span>
                        {isSelf && (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100 text-[9px] font-extrabold text-indigo-700 uppercase">
                            Anda
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate font-mono mt-0.5">{usr.email}</p>
                    </div>
                  </div>
                </td>

                {/* Role Badge */}
                <td className="py-3.5 px-5">
                  <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${roleStyles}`}>
                    {usr.role}
                  </span>
                </td>

                {/* Organization */}
                <td className="py-3.5 px-5">
                  <span className="font-medium text-slate-700 truncate block max-w-[160px]" title={usr.organization || '-'}>
                    {usr.organization || '-'}
                  </span>
                </td>

                {/* Publisher Code */}
                <td className="py-3.5 px-5">
                  {usr.publisherCode ? (
                    <span className="font-mono bg-slate-100 border border-slate-200 text-slate-700 font-bold px-1.5 py-0.5 rounded text-[10px]">
                      {usr.publisherCode}
                    </span>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </td>

                {/* Verification Status with Click-to-Manage */}
                <td className="py-3.5 px-5">
                  {usr.role === 'penerbit' || usr.role === 'organisasi' ? (
                    onOpenPublisherVerification ? (
                      <button
                        type="button"
                        onClick={() => onOpenPublisherVerification(usr)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-bold transition cursor-pointer shadow-2xs group hover:opacity-90 ${
                          isPubVerified
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                            : isPubPending
                            ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
                            : isPubRejected
                            ? 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100'
                            : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                        }`}
                        title={`Klik untuk mengubah status verifikasi ${usr.role === 'organisasi' ? 'organisasi' : 'penerbit'}`}
                      >
                        {isPubVerified ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : isPubPending ? (
                          <Clock className="w-3.5 h-3.5 text-amber-600" />
                        ) : isPubRejected ? (
                          <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 text-slate-500" />
                        )}
                        <span>
                          {isPubVerified
                            ? 'Terverifikasi'
                            : isPubPending
                            ? 'Menunggu Review'
                            : isPubRejected
                            ? 'Ditolak / Revisi'
                            : 'Belum Verifikasi'}
                        </span>
                        <ChevronDown className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                      </button>
                    ) : isPubVerified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                        <ShieldCheck className="w-3 h-3 text-emerald-600" />
                        <span>Terverifikasi</span>
                      </span>
                    ) : isPubPending ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold">
                        <Clock className="w-3 h-3 text-amber-600" />
                        <span>Menunggu Review</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold">
                        <ShieldAlert className="w-3 h-3 text-rose-600" />
                        <span>Belum Verifikasi</span>
                      </span>
                    )
                  ) : usr.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                      <span>Admin Pusat</span>
                    </span>
                  ) : (
                    <span className="text-slate-400 text-[11px]">-</span>
                  )}
                </td>

                {/* Date Joined */}
                <td className="py-3.5 px-5 font-mono text-slate-400 text-[10px]">
                  {usr.createdAt
                    ? new Date(usr.createdAt).toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : '-'}
                </td>

                {/* Actions */}
                <td className="py-3.5 px-5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {usr.role === 'penerbit' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (onOpenPublisherVerification) {
                            onOpenPublisherVerification(usr);
                          } else if (onToggleVerifyPublisher) {
                            onToggleVerifyPublisher(usr);
                          }
                        }}
                        className={`p-1.5 rounded-lg transition cursor-pointer flex items-center gap-1 text-[10px] font-bold ${
                          isPubVerified
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                        }`}
                        title="Kelola & Ubah Status Verifikasi Penerbit"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        <span className="hidden xl:inline">Ubah Status</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => onOpenEdit(usr)}
                      className="p-1.5 hover:bg-slate-100 hover:text-indigo-600 rounded-lg text-slate-400 transition cursor-pointer"
                      title="Edit Peran & Info"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>

                    <button
                      type="button"
                      onClick={() => onOpenDelete(usr)}
                      disabled={isSelf}
                      className={`p-1.5 rounded-lg transition ${
                        isSelf
                          ? 'text-slate-200 cursor-not-allowed'
                          : 'hover:bg-rose-50 hover:text-rose-600 text-slate-400 cursor-pointer'
                      }`}
                      title={isSelf ? 'Anda tidak bisa menghapus diri sendiri' : 'Hapus User'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
